import { StyleDeclarationValue, StyleSheet, css } from 'aphrodite';
import { AppBarHeight } from '../../web/components/AppBar/styles';
import { FullscreenModalCloseButtonInset } from '../../web/components/fullscreen/FullscreenModal/styles';
import { NAV_BAR_WIDTH_COMPACT, NAV_BAR_WIDTH_EXPANDED } from '../../web/constants/sizes';
import { CssSelectors, scrollbarBase } from '../../web/styles/styles';
import { brandPrimaryHover } from '../styles/colors';

/**
 * Coffee's grid. A sibling of main lev's `AppGrid`, not a variant of it — Coffee carries a sub-bar row
 * (dialer + gamification) that main lev will never have, and expressing both topologies in one template
 * makes neither readable.
 *
 * What must not drift is the dimensions, so every one is imported from the same place main lev reads it.
 * Topology is free to differ; sizes are not.
 */

export const CoffeeAppGrid = ({ children }: { children: React.ReactNode }) => {
	return <div className={css(styles.grid)}>{children}</div>;
};
export const CoffeeNavArea = ({ children }: { children: React.ReactNode }) => {
	return <div className={css(styles.nav)}>{children}</div>;
};
export const CoffeeWarningArea = ({ children }: { children: React.ReactNode }) => {
	return <div className={css(styles.warning)}>{children}</div>;
};
export const CoffeeAppBarArea = ({ children }: { children: React.ReactNode }) => {
	return <div className={css(styles.appBar)}>{children}</div>;
};
/** Collapses to zero height when it has no children — settings and reporting render no sub-bar. */
export const CoffeeSubBarArea = ({ children }: { children?: React.ReactNode }) => {
	return <div className={css(styles.subBar)}>{children}</div>;
};
export const CoffeePageArea = ({
	children,
	styles: overrides = [],
}: {
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}) => {
	return (
		<div data-scroll-restoration-id='aida-main-body' className={css(styles.page, ...overrides)}>
			{children}
		</div>
	);
};

const styles = StyleSheet.create({
	grid: {
		display: 'grid',
		maxHeight: '100vh',
		maxWidth: '100vw',
		minHeight: '100vh',
		minWidth: '100vw',
		overflow: 'hidden',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		gridTemplateRows: 'auto auto auto 1fr',
		gridTemplateAreas: `
		"navigation warning"
		"navigation app-bar"
		"navigation sub-bar"
		"navigation content-root"
		`,
		// Coffee keeps its blue rather than adopting main lev's light page background. Pages are built
		// against the blue, so retaining it is what makes them migrate without a rewrite.
		backgroundColor: brandPrimaryHover,
	},
	warning: {
		display: 'flex',
		flexDirection: 'column',
		gridArea: 'warning',
		// Same inset as main lev's `AppGrid`, for the same reason — see the comment there before changing
		// this: margin rather than padding, so the full-width box stops swallowing clicks on the modal's X.
		marginRight: FullscreenModalCloseButtonInset,
		minHeight: 0,
		position: 'relative',
		zIndex: 1000,
	},
	nav: {
		display: 'flex',
		gridArea: 'navigation',
		minWidth: NAV_BAR_WIDTH_COMPACT,
		maxWidth: NAV_BAR_WIDTH_EXPANDED,
		flexDirection: 'column',
		overflow: 'hidden',
	},
	appBar: {
		display: 'flex',
		gridArea: 'app-bar',
		width: '100%',
		overflow: 'hidden',
		maxHeight: AppBarHeight,
	},
	subBar: {
		display: 'flex',
		gridArea: 'sub-bar',
		width: '100%',
		minHeight: 0,
		// The dialpad opens downward out of this row, so it must not clip, and must paint above the page.
		overflow: 'visible',
		position: 'relative',
		zIndex: 4,
	},
	page: {
		gridArea: 'content-root',
		overflowY: 'auto',
		display: 'flex',
		height: '100%',
		minHeight: 0,
		...scrollbarBase,
		...CssSelectors.allChildren({ flex: 1 }),
	},
});
